import type { GamificationTrophyItem } from '@crowdfpl/domain/gamification';

const ALMOST_THERE_MIN_PROGRESS = 0.5;
const ALMOST_THERE_LIMIT = 3;

/**
 * Not-maxed trophies with API `next_progress` >= 50%, sorted by progress DESC
 * then trophy_id ASC, capped at three. Never recomputes server metrics/rarity.
 */
export function selectAlmostThereTrophies(
  trophies: readonly GamificationTrophyItem[],
): GamificationTrophyItem[] {
  return trophies
    .filter(trophy => {
      if (trophy.status === 'maxed') {
        return false;
      }
      if (trophy.next_progress == null) {
        return false;
      }
      return trophy.next_progress >= ALMOST_THERE_MIN_PROGRESS;
    })
    .slice()
    .sort((left, right) => {
      const progressDiff = (right.next_progress ?? 0) - (left.next_progress ?? 0);
      if (progressDiff !== 0) {
        return progressDiff;
      }
      return left.trophy_id.localeCompare(right.trophy_id);
    })
    .slice(0, ALMOST_THERE_LIMIT);
}

export function isTrophyUnlocked(trophy: GamificationTrophyItem): boolean {
  if (trophy.kind === 'one_shot') {
    return trophy.status === 'unlocked' || trophy.status === 'maxed';
  }

  return (
    trophy.highest_tier_index != null ||
    trophy.status === 'unlocked' ||
    trophy.status === 'maxed'
  );
}

/**
 * Featured trophy is server effective pin only. Never pick first unlocked locally
 * (auto-pin comparator stays server-side).
 */
export function resolveFeaturedTrophy(
  trophies: readonly GamificationTrophyItem[],
  pinnedTrophyId: string | null | undefined,
): GamificationTrophyItem | null {
  if (!pinnedTrophyId) {
    return null;
  }

  return (
    trophies.find(trophy => trophy.trophy_id === pinnedTrophyId && isTrophyUnlocked(trophy)) ?? null
  );
}

export type TrophySystemSections = {
  almostThere: GamificationTrophyItem[];
  yourTrophies: GamificationTrophyItem[];
  locked: GamificationTrophyItem[];
};

/**
 * Build modal sections while preserving server array order inside unlocked/locked.
 */
export function buildTrophySystemSections(
  trophies: readonly GamificationTrophyItem[],
): TrophySystemSections {
  return {
    almostThere: selectAlmostThereTrophies(trophies),
    yourTrophies: trophies.filter(isTrophyUnlocked),
    locked: trophies.filter(trophy => !isTrophyUnlocked(trophy)),
  };
}
